import React, { useState } from 'react';
import { TaxConfigurationService } from '../../../services/taxConfigurationService';

const ComplianceTracker = ({ complianceRules, jurisdictions, organizationId, onUpdate }) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [formData, setFormData] = useState({
    ruleName: '',
    jurisdictionId: '',
    ruleType: 'sales_tax',
    filingFrequency: 'monthly',
    filingDeadlineDay: 15,
    filingDeadlineMonth: 1,
    thresholdAmount: '',
    automatedCalculation: true,
    notificationDaysBefore: 7,
    nextFilingDate: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const ruleTypes = [
    { value: 'sales_tax', label: 'Sales Tax' },
    { value: 'vat', label: 'VAT' },
    { value: 'income_tax', label: 'Income Tax' },
    { value: 'property_tax', label: 'Property Tax' },
    { value: 'payroll_tax', label: 'Payroll Tax' },
    { value: 'customs_duty', label: 'Customs Duty' },
    { value: 'excise_tax', label: 'Excise Tax' },
    { value: 'other', label: 'Other' }
  ];

  const filingFrequencies = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'semi_annually', label: 'Semi-Annually' },
    { value: 'annually', label: 'Annually' },
    { value: 'on_demand', label: 'On Demand' }
  ];

  const getStatusBadge = (status) => {
    const statusStyles = {
      compliant: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      not_applicable: 'bg-gray-100 text-gray-800'
    };

    const statusLabels = {
      compliant: 'Compliant',
      pending: 'Pending',
      overdue: 'Overdue',
      not_applicable: 'Not Applicable'
    };

    return (
      <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${statusStyles?.[status] || statusStyles?.pending}`}>
        {statusLabels?.[status] || status}
      </span>
    );
  };

  const getUrgencyLevel = (nextFilingDate, notificationDaysBefore) => {
    if (!nextFilingDate) return 'none';
    
    const daysUntilDue = Math.ceil((new Date(nextFilingDate) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= notificationDaysBefore) return 'urgent';
    if (daysUntilDue <= notificationDaysBefore * 2) return 'soon';
    return 'normal';
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyStyles = {
      overdue: 'bg-red-500 text-white',
      urgent: 'bg-orange-500 text-white',
      soon: 'bg-yellow-500 text-white',
      normal: 'bg-green-500 text-white',
      none: 'bg-gray-400 text-white'
    };

    const urgencyLabels = {
      overdue: 'Overdue',
      urgent: 'Urgent',
      soon: 'Due Soon',
      normal: 'On Track',
      none: 'No Date'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${urgencyStyles?.[urgency]}`}>
        {urgencyLabels?.[urgency]}
      </span>
    );
  };

  const handleCreate = () => {
    setSelectedRule(null);
    setFormData({
      ruleName: '',
      jurisdictionId: '',
      ruleType: 'sales_tax',
      filingFrequency: 'monthly',
      filingDeadlineDay: 15,
      filingDeadlineMonth: 1,
      thresholdAmount: '',
      automatedCalculation: true,
      notificationDaysBefore: 7,
      nextFilingDate: ''
    });
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setFormData({
      ruleName: rule?.ruleName || '',
      jurisdictionId: rule?.jurisdictionId || '',
      ruleType: rule?.ruleType || 'sales_tax',
      filingFrequency: rule?.filingFrequency || 'monthly',
      filingDeadlineDay: rule?.filingDeadlineDay || 15,
      filingDeadlineMonth: rule?.filingDeadlineMonth || 1,
      thresholdAmount: rule?.thresholdAmount?.toString() || '',
      automatedCalculation: rule?.automatedCalculation !== undefined ? rule?.automatedCalculation : true,
      notificationDaysBefore: rule?.notificationDaysBefore || 7,
      nextFilingDate: rule?.nextFilingDate || ''
    });
    setErrors({});
    setShowForm(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.ruleName?.trim()) {
      newErrors.ruleName = 'Rule name is required';
    }

    if (!formData?.jurisdictionId) {
      newErrors.jurisdictionId = 'Jurisdiction is required';
    }

    if (formData?.filingDeadlineDay < 1 || formData?.filingDeadlineDay > 31) {
      newErrors.filingDeadlineDay = 'Filing deadline day must be between 1 and 31';
    }

    if (formData?.filingDeadlineMonth < 1 || formData?.filingDeadlineMonth > 12) {
      newErrors.filingDeadlineMonth = 'Filing deadline month must be between 1 and 12';
    }

    if (formData?.thresholdAmount && (isNaN(parseFloat(formData?.thresholdAmount)) || parseFloat(formData?.thresholdAmount) < 0)) {
      newErrors.thresholdAmount = 'Threshold amount must be a valid positive number';
    }

    if (formData?.notificationDaysBefore < 0 || formData?.notificationDaysBefore > 365) {
      newErrors.notificationDaysBefore = 'Notification days must be between 0 and 365';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        organizationId,
        thresholdAmount: formData?.thresholdAmount ? parseFloat(formData?.thresholdAmount) : null
      };

      if (selectedRule) {
        // Update functionality would be implemented here
        console.log('Update rule:', submitData);
      } else {
        await TaxConfigurationService?.createComplianceRule(submitData);
      }
      
      setShowForm(false);
      setSelectedRule(null);
      await onUpdate();
    } catch (err) {
      console.error('Error saving compliance rule:', err);
      setErrors({ submit: err?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const getDaysUntilDue = (nextFilingDate) => {
    if (!nextFilingDate) return null;
    const days = Math.ceil((new Date(nextFilingDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Sort rules by urgency (overdue first, then by days until due)
  const sortedRules = [...complianceRules]?.sort((a, b) => {
    const aUrgency = getUrgencyLevel(a?.nextFilingDate, a?.notificationDaysBefore);
    const bUrgency = getUrgencyLevel(b?.nextFilingDate, b?.notificationDaysBefore);
    
    const urgencyOrder = { overdue: 0, urgent: 1, soon: 2, normal: 3, none: 4 };
    
    if (urgencyOrder?.[aUrgency] !== urgencyOrder?.[bUrgency]) {
      return urgencyOrder?.[aUrgency] - urgencyOrder?.[bUrgency];
    }
    
    // If same urgency, sort by days until due
    const aDays = getDaysUntilDue(a?.nextFilingDate) || 999;
    const bDays = getDaysUntilDue(b?.nextFilingDate) || 999;
    return aDays - bDays;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Tax Compliance Tracker</h2>
            <p className="mt-1 text-sm text-gray-600">
              Monitor filing deadlines and compliance requirements
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Add Compliance Rule</span>
          </button>
        </div>
      </div>
      {/* Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-2xl font-semibold text-green-600">
                {complianceRules?.filter(r => r?.status === 'compliant')?.length}
              </p>
              <p className="text-sm text-gray-600">Compliant</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">⏳</span>
            </div>
            <div className="ml-4">
              <p className="text-2xl font-semibold text-yellow-600">
                {complianceRules?.filter(r => r?.status === 'pending')?.length}
              </p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">❌</span>
            </div>
            <div className="ml-4">
              <p className="text-2xl font-semibold text-red-600">
                {complianceRules?.filter(r => r?.status === 'overdue')?.length}
              </p>
              <p className="text-sm text-gray-600">Overdue</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">🚨</span>
            </div>
            <div className="ml-4">
              <p className="text-2xl font-semibold text-orange-600">
                {complianceRules?.filter(r => {
                  const urgency = getUrgencyLevel(r?.nextFilingDate, r?.notificationDaysBefore);
                  return urgency === 'urgent' || urgency === 'overdue';
                })?.length}
              </p>
              <p className="text-sm text-gray-600">Urgent</p>
            </div>
          </div>
        </div>
      </div>
      {/* Compliance Rules List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Compliance Rules</h3>
        </div>
        
        {sortedRules?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="flex flex-col items-center">
              <span className="text-4xl mb-4">📋</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No compliance rules found</h3>
              <p className="text-sm text-gray-500 mb-4">
                Get started by creating your first compliance rule
              </p>
              <button
                onClick={handleCreate}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Create Compliance Rule
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sortedRules?.map((rule) => {
              const urgency = getUrgencyLevel(rule?.nextFilingDate, rule?.notificationDaysBefore);
              const daysUntilDue = getDaysUntilDue(rule?.nextFilingDate);
              
              return (
                <div key={rule?.id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-lg font-medium text-gray-900">{rule?.ruleName}</h4>
                        {getUrgencyBadge(urgency)}
                        {getStatusBadge(rule?.status)}
                      </div>
                      
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Jurisdiction</p>
                          <p className="text-sm font-medium text-gray-900">{rule?.jurisdictionName || 'Unknown'}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-500">Rule Type</p>
                          <p className="text-sm font-medium text-gray-900">
                            {ruleTypes?.find(t => t?.value === rule?.ruleType)?.label || rule?.ruleType}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-500">Filing Frequency</p>
                          <p className="text-sm font-medium text-gray-900">
                            {filingFrequencies?.find(f => f?.value === rule?.filingFrequency)?.label || rule?.filingFrequency}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-500">Next Filing Date</p>
                          <p className="text-sm font-medium text-gray-900">
                            {rule?.nextFilingDate ? new Date(rule.nextFilingDate)?.toLocaleDateString() : 'Not set'}
                            {daysUntilDue !== null && (
                              <span className={`ml-2 text-xs ${
                                daysUntilDue < 0 ? 'text-red-600' : 
                                daysUntilDue <= rule?.notificationDaysBefore ? 'text-orange-600': 'text-gray-500'
                              }`}>
                                ({daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days overdue` : `${daysUntilDue} days`})
                              </span>
                            )}
                          </p>
                        </div>
                        
                        {rule?.thresholdAmount && (
                          <div>
                            <p className="text-sm text-gray-500">Threshold Amount</p>
                            <p className="text-sm font-medium text-gray-900">{formatCurrency(rule?.thresholdAmount)}</p>
                          </div>
                        )}
                        
                        <div>
                          <p className="text-sm text-gray-500">Notification</p>
                          <p className="text-sm font-medium text-gray-900">{rule?.notificationDaysBefore} days before</p>
                        </div>
                      </div>
                      
                      {rule?.lastFiledDate && (
                        <div className="mt-3 text-sm text-gray-500">
                          Last filed: {new Date(rule.lastFiledDate)?.toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleEdit(rule)}
                      className="ml-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      title="Edit compliance rule"
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedRule ? 'Edit Compliance Rule' : 'Create New Compliance Rule'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="ruleName" className="block text-sm font-medium text-gray-700 mb-2">
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    id="ruleName"
                    value={formData?.ruleName}
                    onChange={(e) => handleInputChange('ruleName', e?.target?.value)}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.ruleName 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="e.g., California Sales Tax Filing"
                  />
                  {errors?.ruleName && (
                    <p className="mt-1 text-sm text-red-600">{errors?.ruleName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="jurisdictionId" className="block text-sm font-medium text-gray-700 mb-2">
                    Jurisdiction *
                  </label>
                  <select
                    id="jurisdictionId"
                    value={formData?.jurisdictionId}
                    onChange={(e) => handleInputChange('jurisdictionId', e?.target?.value)}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.jurisdictionId 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  >
                    <option value="">Select a jurisdiction</option>
                    {jurisdictions?.map((jurisdiction) => (
                      <option key={jurisdiction?.id} value={jurisdiction?.id}>
                        {jurisdiction?.name} ({jurisdiction?.jurisdictionType})
                      </option>
                    ))}
                  </select>
                  {errors?.jurisdictionId && (
                    <p className="mt-1 text-sm text-red-600">{errors?.jurisdictionId}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="ruleType" className="block text-sm font-medium text-gray-700 mb-2">
                    Rule Type
                  </label>
                  <select
                    id="ruleType"
                    value={formData?.ruleType}
                    onChange={(e) => handleInputChange('ruleType', e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {ruleTypes?.map((type) => (
                      <option key={type?.value} value={type?.value}>
                        {type?.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="filingFrequency" className="block text-sm font-medium text-gray-700 mb-2">
                    Filing Frequency
                  </label>
                  <select
                    id="filingFrequency"
                    value={formData?.filingFrequency}
                    onChange={(e) => handleInputChange('filingFrequency', e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {filingFrequencies?.map((frequency) => (
                      <option key={frequency?.value} value={frequency?.value}>
                        {frequency?.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="filingDeadlineDay" className="block text-sm font-medium text-gray-700 mb-2">
                    Filing Deadline Day
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    id="filingDeadlineDay"
                    value={formData?.filingDeadlineDay}
                    onChange={(e) => handleInputChange('filingDeadlineDay', parseInt(e?.target?.value))}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.filingDeadlineDay 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                  {errors?.filingDeadlineDay && (
                    <p className="mt-1 text-sm text-red-600">{errors?.filingDeadlineDay}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="thresholdAmount" className="block text-sm font-medium text-gray-700 mb-2">
                    Threshold Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    id="thresholdAmount"
                    value={formData?.thresholdAmount}
                    onChange={(e) => handleInputChange('thresholdAmount', e?.target?.value)}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.thresholdAmount 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                    placeholder="0.00"
                  />
                  {errors?.thresholdAmount && (
                    <p className="mt-1 text-sm text-red-600">{errors?.thresholdAmount}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="notificationDaysBefore" className="block text-sm font-medium text-gray-700 mb-2">
                    Notification Days Before
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    id="notificationDaysBefore"
                    value={formData?.notificationDaysBefore}
                    onChange={(e) => handleInputChange('notificationDaysBefore', parseInt(e?.target?.value))}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
                      errors?.notificationDaysBefore 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' :'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                  {errors?.notificationDaysBefore && (
                    <p className="mt-1 text-sm text-red-600">{errors?.notificationDaysBefore}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="nextFilingDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Next Filing Date
                  </label>
                  <input
                    type="date"
                    id="nextFilingDate"
                    value={formData?.nextFilingDate}
                    onChange={(e) => handleInputChange('nextFilingDate', e?.target?.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="automatedCalculation"
                  checked={formData?.automatedCalculation}
                  onChange={(e) => handleInputChange('automatedCalculation', e?.target?.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="automatedCalculation" className="ml-2 text-sm text-gray-700">
                  Enable Automated Calculation
                </label>
              </div>

              {errors?.submit && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-600">{errors?.submit}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    loading 
                      ? 'bg-gray-400 cursor-not-allowed' :'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Saving...' : (selectedRule ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceTracker;